// src/components/PitchReels/SkeletonLoaders.jsx
import React from 'react';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

// Desktop Video Card Skeleton
const DesktopVideoSkeleton = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100dvh',
    minHeight: 'calc(100dvh - 4rem)',
    scrollSnapAlign: 'start',
    position: 'relative',
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: theme.spacing(4),
}));

// Mobile Video Card Skeleton
const MobileVideoSkeleton = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: '100%',
    height: '100dvh',
    minHeight: '100dvh',
    scrollSnapAlign: 'start',
    position: 'relative',
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
}));

export const VideoCardSkeletonMobile = () => {
    return (
        <MobileVideoSkeleton>
            {/* Video placeholder */}
            <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                animation="wave"
                sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    position: 'absolute',
                    top: 0,
                    left: 0
                }}
            />

            {/* Action buttons (right side) */}
            <Box
                sx={{
                    position: 'absolute',
                    right: 16,
                    bottom: '16rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    zIndex: 2,
                }}
            >
                {/* Shortlist button */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Skeleton
                        variant="circular"
                        width={48}
                        height={48}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                    />
                    <Skeleton
                        variant="text"
                        width={50}
                        height={12}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                </Box> */}

                {/* Reject button */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Skeleton
                        variant="circular"
                        width={48}
                        height={48}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                    />
                    <Skeleton
                        variant="text"
                        width={40}
                        height={12}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                </Box> */}

                {/* View Deck button */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Skeleton
                        variant="circular"
                        width={48}
                        height={48}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                    />
                    <Skeleton
                        variant="text"
                        width={55}
                        height={12}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                </Box> */}
            </Box>

            {/* Bottom content overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '1.5rem 1rem 6rem 1rem',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
                    zIndex: 1,
                }}
            >
                {/* Logo and Company Info */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    mb: 2
                }}>
                    {/* Company Logo */}
                    <Skeleton
                        variant="circular"
                        width={48}
                        height={48}
                        animation="wave"
                        sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            flexShrink: 0
                        }}
                    />
                    
                    {/* Company Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Company Name */}
                        <Skeleton
                            variant="text"
                            width="60%"
                            height={20}
                            animation="wave"
                            sx={{ 
                                mb: 0.5, 
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: '4px'
                            }}
                        />
                        {/* Tagline/Category */}
                        <Skeleton
                            variant="text"
                            width="80%"
                            height={16}
                            animation="wave"
                            sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '4px'
                            }}
                        />
                    </Box>
                </Box>

                {/* Description Lines */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Skeleton
                        variant="text"
                        width="95%"
                        height={18}
                        animation="wave"
                        sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            borderRadius: '4px'
                        }}
                    />
                    <Skeleton
                        variant="text"
                        width="88%"
                        height={18}
                        animation="wave"
                        sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            borderRadius: '4px'
                        }}
                    />
                </Box>
            </Box>
        </MobileVideoSkeleton>
    );
};

export const VideoCardSkeletonDesktop = () => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: "space-between",
                width: 'calc(100% - 2rem)',
                height: {xs: 'calc(100dvh - 5rem)', sm: 'calc(100dvh - 4rem)', md: 'calc(100dvh - 5rem)'},
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
                overflow: "hidden",
                padding: "0rem 1.3rem"
            }}
        >

            {/* Right Side - Video Section */}
            <Box
                sx={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: "1rem"
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        // width: '100%',
                        height: '100%',
                        maxWidth: '500px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: "9 / 16"
                    }}
                >
                    {/* Video Player Skeleton */}
                    <Skeleton
                        variant="rounded"
                        width="100%"
                        height="100%"
                        animation="wave"
                        sx={{
                            backgroundColor: '#000',
                            borderRadius: '16px',
                            maxWidth: '500px',
                        }}
                    />

                    {/* Play Button Overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <Skeleton
                            variant="circular"
                            width={100}
                            height={100}
                            animation="wave"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            }}
                        />
                    </Box>

                    {/* Mute Button */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                        }}
                    >
                        <Skeleton
                            variant="circular"
                            width={40}
                            height={40}
                            animation="wave"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};


// Main skeleton feed component
export const PitchReelsFeedSkeleton = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const SkeletonContainer = styled(Box)(({ theme }) => ({
        width: '100%',
        maxWidth: isDesktop ? '100%' : 'auto',
        height: 'calc(100dvh - 3rem)',
        margin: '0 auto',
        overflowY: 'hidden',
        // backgroundColor: '#000',
        position: 'relative',
        [theme.breakpoints.up('md')]: {
            maxWidth: '100%',
            // paddingLeft: "2rem"
        },
    }));

    return (
        <SkeletonContainer>
            {isDesktop ? (
                <>
                    <VideoCardSkeletonDesktop />
                </>
            ) : (
                <>
                    <VideoCardSkeletonMobile />
                </>
            )}
        </SkeletonContainer>
    );
};
